import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Skeleton } from '@mui/material';
import { getReportPatterns } from '../../services/reportService';
import { getOutcomeChip } from '../../utils/reportTaxonomy';
import { BRAND } from '../../theme/brandTheme';

/**
 * Similar tests on ReportDetail — matches shared tags / taxonomy
 */
const SimilarTestsCard = ({ report }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const query = useMemo(() => {
    if (!report?._id) return null;
    const params = { excludeId: report._id, limit: 5 };
    if (report.tags?.length) params.tag = report.tags[0];
    else if (report.pageType) params.pageType = report.pageType;
    else if (report.changeType) params.changeType = report.changeType;
    else return null;
    return params;
  }, [report]);

  useEffect(() => {
    if (!query) {
      setItems([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const data = await getReportPatterns(query);
        setItems(data?.reports || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [query]);

  if (!query) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #eee' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.purple }}>
          Benzer testler
        </Typography>
        <RouterLink
          to={`/dashboard/library?${new URLSearchParams(
            Object.entries(query).filter(([k]) => k !== 'excludeId' && k !== 'limit'),
          ).toString()}`}
          style={{ fontSize: 13, color: BRAND.purple }}
        >
          Kütüphanede gör →
        </RouterLink>
      </Box>

      {loading ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={36} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Henüz benzer etiketli test yok
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {items.map((item) => {
            const outcome = getOutcomeChip(item.resultStatuses);
            return (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <RouterLink
                  to={`/dashboard/reports/${item.id}`}
                  style={{ color: 'inherit', fontWeight: 600, textDecoration: 'none' }}
                >
                  {item.name}
                </RouterLink>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.brand}
                  </Typography>
                  <Chip label={outcome.label} size="small" color={outcome.color} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default SimilarTestsCard;
